/** Errors from the seekr API layer, and the one place a rejected response is
 * read for something to show the visitor.
 *
 * `ApiHttpError` carries the upstream HTTP status so a caller can branch on it.
 * The signin flow needs the 500-vs-4xx distinction to fire the legacy
 * `Sign up form 500 error` event only on a server error, and a general
 * `OTP Signup Failed` otherwise. The message stays the user-safe string the
 * api/ layer already produced, so existing error display is unaffected.
 */

import type { ApiError } from "./types";

class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiHttpError";
  }
}

/** What a rejected seekr response says, if it says anything usable.
 *
 * Two answer shapes, because seekr has two kinds of endpoint: the
 * hand-written views reject with `detail` or `error`, while a DRF serializer
 * rejects per field — `{"email": ["Enter a valid email address."]}`. A caller
 * that reads only the first shape shows a generic failure for the one
 * rejection the visitor could actually have fixed. */
function apiErrorMessage(data: ApiError | undefined): string | undefined {
  return (
    firstMessage(data?.detail) ??
    firstMessage(data?.error) ??
    firstMessage(data?.email) ??
    firstMessage(data?.non_field_errors)
  );
}

/** Read a rejected response's body for `apiErrorMessage`, tolerating a body
 * that isn't JSON at all (an HTML error page, an empty 502). */
async function errorBody(res: Response): Promise<ApiError | undefined> {
  return res.json().then(
    (data: ApiError) => data ?? undefined,
    () => undefined,
  );
}

function firstMessage(value: string | string[] | undefined): string | undefined {
  const message = Array.isArray(value) ? value[0] : value;
  return typeof message === "string" && message.trim() ? message : undefined;
}

export { ApiHttpError, apiErrorMessage, errorBody };
